import { ProgressText } from "@runly/ui";
import { Box, Text } from "ink";
import ProgressBar from "ink-progress-bar";
import Spinner from "ink-spinner";
import { isFinite } from "lodash";
import React, { FC, useEffect } from "react";

import { RunProgress } from "./execution";
import useRunner, { RunAction } from "./execution/hook";
import Results from "./results";

const Runner: FC<RunAction> = ({ onComplete, ...props }) => {
	const { run, error: configError } = useRunner(props);

	useEffect(() => {
		if ((run?.isComplete || configError) && onComplete) {
			onComplete();
		}
	}, [run?.isComplete, configError, onComplete]);

	if (configError) {
		return (
			<Box flexDirection="column">
				<Box>
					<Text bold color="red">
						Unable to parse job config: {props.config.job}
					</Text>
				</Box>
				<Box>
					<Text color="red">{configError.message}</Text>
				</Box>

				{configError.contents ? (
					<Box paddingTop={1}>
						<Text color="red">{configError.contents}</Text>
					</Box>
				) : (
					false
				)}
			</Box>
		);
	}

	if (run?.isRunning) {
		return (
			<Text color={progressToColor(run.progress)}>
				<Progress progress={run.progress} />
			</Text>
		);
	}

	if (run?.isComplete) return <Results run={run} />;

	return null;
};

function Progress({ progress }: { progress: RunProgress }) {
	if (isFinite(progress.total)) {
		const percent =
			(Number(progress.success) + Number(progress.failed)) / progress.total;

		return (
			<>
				<Spinner type="dots" />
				<ProgressBar percent={percent} left={2} />
				<ProgressText progress={progress} component={Text} />
			</>
		);
	}

	return (
		<>
			<Spinner type="dots" />
			<ProgressText progress={progress} component={Text} />
		</>
	);
}

function progressToColor({
	failed,
	success
}: {
	failed?: number;
	success?: number;
}) {
	if (failed === 0) {
		return "green";
	}
	if (success > 0) {
		return "yellow";
	}
	return "red";
}

export default Runner;
